import createDOMPurify, { Config, DOMPurify } from 'dompurify';

const policy: Config = {
  ALLOWED_TAGS: ['p', 'br', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'b', 'i', 'u', 's', 'strike', 'del', 'sup', 'sub', 'small', 'font', 'center', 'blockquote', 'pre', 'code', 'hr', 'a', 'img', 'ul', 'ol', 'li', 'table', 'caption', 'colgroup', 'col', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td'],
  ALLOWED_ATTR: ['class', 'style', 'dir', 'lang', 'title', 'href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'align', 'valign', 'color', 'face', 'size', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'colspan', 'rowspan', 'scope', 'start', 'reversed', 'type', 'data-group-id', 'aria-label', 'aria-hidden'],
  ALLOWED_NAMESPACES: ['http://www.w3.org/1999/xhtml'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  FORBID_CONTENTS: ['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'template', 'noscript', 'form'],
  RETURN_TRUSTED_TYPE: false
};

const styleProperties = new Set([
  'color', 'background-color', 'background', 'font', 'font-family', 'font-size', 'font-weight', 'font-style', 'font-variant',
  'font-variant-numeric', 'font-variant-east-asian', 'font-variant-alternates', 'font-variant-position', 'font-variant-emoji',
  'line-height', 'letter-spacing', 'word-spacing', 'text-align', 'text-indent', 'text-transform', 'text-decoration',
  'text-decoration-line', 'text-decoration-color', 'text-decoration-style', 'text-decoration-thickness', 'text-decoration-skip-ink',
  'white-space', 'white-space-collapse', 'text-wrap-mode', 'overflow-wrap', 'word-break', 'vertical-align',
  'width', 'height', 'min-width', 'max-width', 'min-height', 'max-height', 'box-sizing',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left', 'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'border', 'border-width', 'border-style', 'border-color', 'border-top', 'border-right', 'border-bottom', 'border-left',
  'border-radius', 'border-collapse', 'border-spacing', 'table-layout', 'caption-side', 'list-style-type', 'list-style-position',
  'display', 'float', 'clear', 'object-fit', 'object-position', 'caret-color'
]);
const styleFunctions = new Set(['rgb', 'rgba', 'hsl', 'hsla', 'calc', 'min', 'max', 'clamp', 'linear-gradient', 'radial-gradient', 'repeating-linear-gradient', 'repeating-radial-gradient']);
const rasterData = /^data:image\/(?:png|jpeg|gif|webp|avif);base64,[a-z0-9+/\s]+=*$/i;
let purifier: DOMPurify | undefined;
let styleCache = new Map<string, string>();

/** 링크/이미지 입력 경계에서도 사용하는 URL 정책. 상대 주소와 일반 래스터 이미지를 보존한다. */
export function isSafePromotionUrl(value: string, kind: 'link' | 'image'): boolean {
  if (!value.trim()) return false;
  if (kind === 'image' && rasterData.test(value.trim())) return true;
  try {
    const url = new URL(value, 'https://promotion.invalid/');
    return !url.username && !url.password && (kind === 'image' ? ['http:', 'https:'] : ['http:', 'https:', 'mailto:', 'tel:']).includes(url.protocol);
  } catch { return false; }
}

function getPurifier(): DOMPurify | undefined {
  if (purifier || typeof window === 'undefined') return purifier;
  const instance = createDOMPurify(window);
  const parser = document.createElement('span').style;
  instance.addHook('uponSanitizeAttribute', (_element, attribute) => {
    const { attrName, attrValue } = attribute;
    if (attrName === 'href' || attrName === 'src') {
      attribute.keepAttr = isSafePromotionUrl(attrValue, attrName === 'src' ? 'image' : 'link');
    } else if (attrName === 'target') {
      attribute.keepAttr = ['_blank', '_self'].includes(attrValue);
    } else if (attrName === 'style') {
      let clean = styleCache.get(attrValue);
      if (clean === undefined) {
        // CSSOM으로 선언을 파싱하고 URL·실행 함수·전역 배치 속성은 허용하지 않는다.
        parser.cssText = attrValue;
        const declarations: string[] = [];
        for (let index = 0; index < parser.length; index++) {
          const property = parser.item(index);
          const value = parser.getPropertyValue(property);
          const functions = Array.from(value.matchAll(/([a-z-]+)\s*\(/gi), match => match[1].toLowerCase());
          if (!styleProperties.has(property) || /[\\@<>]/.test(value) || functions.some(name => !styleFunctions.has(name))) continue;
          declarations.push(`${property}:${value}${parser.getPropertyPriority(property) ? ' !important' : ''}`);
        }
        clean = declarations.join(';');
        styleCache.set(attrValue, clean);
      }
      attribute.attrValue = clean;
      attribute.keepAttr = clean.length > 0;
    }
  });
  instance.addHook('afterSanitizeAttributes', element => {
    if (element instanceof window.Element && element.tagName === 'A' && element.getAttribute('target') === '_blank') {
      element.setAttribute('rel', 'noopener noreferrer');
    }
  });
  purifier = instance;
  return purifier;
}

/** 커스텀 문법 변환이 끝난 HTML을 DOM 삽입 직전에 정화한다. 저장 원문에는 쓰지 않는다. */
export function sanitizePromotionHtml(html: string): string {
  if (!html) return '';
  const instance = getPurifier();
  if (!instance?.isSupported) return '';
  styleCache = new Map();
  try { return instance.sanitize(html, policy); }
  finally { styleCache.clear(); }
}

/** 편집 중에는 커스텀 제목 구분자를 HTML 태그로 해석하지 않고 문자로 유지한다. */
export function sanitizePromotionEditorHtml(html: string): string {
  return sanitizePromotionHtml(html.replace(/<</g, '&lt;&lt;').replace(/>>/g, '&gt;&gt;'));
}
